import {  Inject, Injectable } from '@angular/core';
import { HttpHeaders, HttpClient, HttpEvent } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { APIFilter } from './apiFilter';


export interface APIPostResponse {
  result: boolean,
  data?: any,
  object?: any,
  objectId?: number
}

export interface APIPatchResponse {
  result: boolean,
  data?: any,
  object?: any,
}

export interface APIPutResponse {
  result: boolean,
  data?: any
}

export interface APIUploadResponse {
  result: boolean,
  data?: any
}

export interface APIGetResponse {
  result: boolean,
  data?: any
}

export interface APIDeleteResponse {
  result: boolean,
  data?: any
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  protected apiUrl$: string;
  protected headers: {name: string, value: string}[] = [];

  constructor(
    @Inject('apiUrl') apiUrl: string,
    protected http: HttpClient
  ) {
    this.apiUrl$ = apiUrl;
  }

  addHeader(name: string, value: string) {
    this.headers = this.headers.filter(h => h.name !== name);
    if (value) {
      this.headers.push({name, value});
    }
  }

  removeHeader(name: string) {
    this.headers = this.headers.filter(h => h.name !== name);
  }

  public test() {
    console.warn('api test called');
    return '';
  }
  public getCustomHeaders(dataType: 'json' | 'formData' = 'json'): HttpHeaders {
    let headers = new HttpHeaders();
    for (let h of this.headers) {
      headers = headers.set(h.name, h.value);
    }

    if (dataType === 'json') {
      return headers.set('Content-Type', 'application/json');
    } else {
      return headers;
    }
  }

  public download(servicename: string, resourceId?: string, filters?: APIFilter, action?: string): void {
    const endpoint = this.createEndpoint(servicename, resourceId || null, action, filters);

    const requestHeaders = {
      headers: this.getCustomHeaders(),
      responseType: 'blob' as 'json',
      observe: 'response' as 'body'
    };

    this.http.get<any>(endpoint, requestHeaders).subscribe(
      (response: any) => {
        const disposition = (response.headers as HttpHeaders).get('content-disposition');
        // Determine filename from response headers
        let filename = null
        if (disposition && disposition.indexOf('attachment') !== -1) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(disposition);
          if (matches !== null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }

        const binaryData = [];
        binaryData.push(response.body);
        const downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(
          new Blob(binaryData, { type: response.type })
        );

        // Only if a filename is set, add the download attribute
        if (filename) {
          downloadLink.setAttribute('download', filename);
        }

        // Add the downloadlink and click to download
        document.body.appendChild(downloadLink);
        downloadLink.click();
        // @todo Remove downloadlink
      });
  }

  public get(servicename: string, resourceId?: string, filters?: APIFilter, action?: string, model?: string) {
    return this.http.get<any>(
      this.createEndpoint(servicename, resourceId || null, action, filters, model),
      { headers: this.getCustomHeaders() }
    ).pipe(
      catchError(this.handleError<any>('get'))
    ) as Observable<APIGetResponse>;
  }

  public post(servicename: string, data: any, resourceId?: string, action?: string, model?: string): Observable<APIPostResponse> {
    return this.http.post(
      this.createEndpoint(servicename, resourceId || null, action, null, model),
      JSON.stringify(data),
      { headers: this.getCustomHeaders() }
    ) as Observable<APIPostResponse>;
  }

  public upload(servicename: string, data: FormData, resourceId?: string, action?: string, model?: string): Observable<APIUploadResponse> {
    return this.http.post(
      this.createEndpoint(servicename, resourceId || null, action, undefined, model),
      data,
      { headers: this.getCustomHeaders('formData') }
    ) as Observable<APIUploadResponse>;
  }

  public uploadPut(servicename: string, data: FormData, resourceId?: string, action?: string, model?: string): Observable<APIUploadResponse> {
    return this.http.put(
      this.createEndpoint(servicename, resourceId || null, action, undefined, model),
      data,
      { headers: this.getCustomHeaders('formData') }
    ) as Observable<APIUploadResponse>;
  }

  public uploadWithProgress(servicename: string, data: FormData, resourceId?: string, action?: string, model?: string): Observable<HttpEvent<APIUploadResponse>> {
    return this.http.post(
      this.createEndpoint(servicename, resourceId || null, action, undefined, model),
      data,
      { headers: this.getCustomHeaders('formData'), reportProgress: true, observe: 'events' }
    ) as Observable<HttpEvent<APIUploadResponse>>;
  }

  public patch(servicename: string, data: any, resourceId?: string, action?: string, model?: string): Observable<APIPatchResponse> {
    return this.http.patch(
      this.createEndpoint(servicename, resourceId || null, action, null, model),
      JSON.stringify(data),
      { headers: this.getCustomHeaders() }
    ) as Observable<APIPatchResponse>;
  }

  public put(servicename: string, data: any, resourceId?: string, action?: string, model?: string): Observable<APIPutResponse> {
    return this.http.put(
      this.createEndpoint(servicename, resourceId || null, action, null, model),
      JSON.stringify(data),
      { headers: this.getCustomHeaders() }
    ) as Observable<APIPutResponse>;
  }

  public delete(servicename: string, resourceId: string, action: string | undefined = undefined): Observable<APIDeleteResponse> {
    return this.http.delete<any>(
      this.createEndpoint(servicename, resourceId, action),
      { headers: this.getCustomHeaders() }
    ) as Observable<APIDeleteResponse>;
  }

  protected createEndpoint(serviceName: string, resourceId: string | null, action: string | null = null, filters: APIFilter | null = null, model?: string) {
     const params = filters ? filters.queryParameters() : [];
     if (model) {params.push('model='+model)};

     return this.apiUrl$
      + serviceName
      + (resourceId ? '/' + resourceId : '')
      + ((resourceId && action) ? '/' + action : '')
      + (params ? '?' + params.join('&') : '')
     ;
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  protected handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      console.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
}
